/**
 * Visual Blocks – mobile sticky horizontal scroll ("scroll-jacking").
 *
 * On mobile: while the section is pinned to the viewport, the user's vertical
 * scroll is mapped to a horizontal translation of the blocks. After the last
 * block is reached, the page continues to scroll vertically as normal.
 * On desktop the enhancement is fully torn down and the layout falls back to
 * the default CSS grid/flex behaviour.
 */

const MOBILE_MQ = '(max-width: 749px)';
const SECTION_SELECTOR = '.js-visual-blocks';

// Length of the "hold" (dead-zone) between blocks, expressed as a fraction of
// the viewport height. e.g. 0.5 means the user must scroll half a screen with
// nothing moving before the next horizontal transition starts.
// Can be overridden per-section via `data-hold-ratio="0.75"`.
const DEFAULT_HOLD_RATIO = 0.5;

class VisualBlocksScroller {
    constructor(root) {
        this.root = root;
        this.viewport = root.querySelector('.visual-blocks__viewport');
        this.track = root.querySelector('.visual-blocks__track');

        if (!this.viewport || !this.track) return;

        this.enabled = false;
        this.distance = 0;
        this.totalScroll = 0;
        this.holdPx = 0;
        this.viewportW = 0;
        this.blockCount = this.track.children.length;
        this.holdRatio = parseFloat(root.dataset.holdRatio);
        if (Number.isNaN(this.holdRatio) || this.holdRatio < 0) {
            this.holdRatio = DEFAULT_HOLD_RATIO;
        }
        this.ticking = false;

        // Bindings
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onMqChange = this.onMqChange.bind(this);
        this.update = this.update.bind(this);

        this.mq = window.matchMedia(MOBILE_MQ);
        if (this.mq.addEventListener) {
            this.mq.addEventListener('change', this.onMqChange);
        } else {
            // Safari < 14 fallback
            this.mq.addListener(this.onMqChange);
        }

        this.onMqChange();
    }

    onMqChange() {
        if (this.mq.matches) {
            this.enable();
        } else {
            this.disable();
        }
    }

    enable() {
        if (this.enabled) return;
        this.enabled = true;
        this.root.classList.add('is-scroll-jacked');

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });

        // Recalculate when the track's size actually changes (images loading,
        // fonts swapping, dynamic content, etc.).
        if ('ResizeObserver' in window) {
            this.ro = new ResizeObserver(() => this.onResize());
            this.ro.observe(this.track);
            this.ro.observe(this.viewport);
        }

        // Images may load after init – recalc when they do.
        this.root.querySelectorAll('img').forEach((img) => {
            if (!img.complete) {
                img.addEventListener('load', this.onResize, { once: true });
            }
        });

        this.measure();
        this.update();
    }

    disable() {
        if (!this.enabled) return;
        this.enabled = false;
        this.root.classList.remove('is-scroll-jacked');

        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);

        if (this.ro) {
            this.ro.disconnect();
            this.ro = null;
        }

        this.root.style.height = '';
        this.track.style.transform = '';
    }

    measure() {
        // Use the sticky viewport's own width instead of window.innerWidth so
        // that any parent padding / scrollbar quirks are accounted for. The
        // track width comes straight from layout (scrollWidth).
        const viewportH = this.viewport.clientHeight || window.innerHeight;
        const viewportW = this.viewport.clientWidth;
        const trackW = this.track.scrollWidth;

        this.viewportW = viewportW;
        this.blockCount = this.track.children.length;
        this.holdPx = viewportH * this.holdRatio;

        // Horizontal distance the track actually needs to travel.
        this.distance = Math.max(0, trackW - viewportW);

        // Scroll timeline is a sequence of slots:
        //   [hold] [transition] [hold] [transition] ... [hold]
        // → blockCount holds + (blockCount - 1) transitions.
        // The transitions together add up to `distance`; holds are dead-zones.
        const holdsTotal = this.blockCount * this.holdPx;
        this.totalScroll = this.distance + holdsTotal;

        this.root.style.height = `${viewportH + this.totalScroll}px`;
    }

    onResize() {
        this.measure();
        this.update();
    }

    onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(this.update);
    }

    update() {
        this.ticking = false;
        if (!this.enabled || this.distance <= 0) {
            this.track.style.transform = 'translate3d(0, 0, 0)';
            return;
        }

        const rect = this.root.getBoundingClientRect();
        const scrolled = Math.min(Math.max(-rect.top, 0), this.totalScroll);

        const x = this.scrollToX(scrolled);
        this.track.style.transform = `translate3d(${-x}px, 0, 0)`;
    }

    /**
     * Convert a vertical scroll distance (past the section top) into a
     * horizontal track offset, respecting the [hold, transition, hold, …]
     * slot sequence.
     */
    scrollToX(scrolled) {
        let remaining = scrolled;
        let x = 0;

        // Leading hold – first block stays put for a moment.
        if (remaining < this.holdPx) return 0;
        remaining -= this.holdPx;

        const transitions = this.blockCount - 1;
        for (let i = 0; i < transitions; i++) {
            // Transition slot: track moves by one viewport width.
            if (remaining < this.viewportW) {
                x += remaining;
                return Math.min(x, this.distance);
            }
            x += this.viewportW;
            remaining -= this.viewportW;

            // Hold slot after this transition.
            if (remaining < this.holdPx) {
                return Math.min(x, this.distance);
            }
            remaining -= this.holdPx;
        }

        return this.distance;
    }

    destroy() {
        this.disable();
        if (this.mq.removeEventListener) {
            this.mq.removeEventListener('change', this.onMqChange);
        } else {
            this.mq.removeListener(this.onMqChange);
        }
    }
}

function init(scope = document) {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
        if (el._visualBlocks) return;
        el.dataset.visualBlocksInit = 'true';
        el._visualBlocks = new VisualBlocksScroller(el);
    });
}

function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
        if (el._visualBlocks) {
            el._visualBlocks.destroy();
            el._visualBlocks = null;
            delete el.dataset.visualBlocksInit;
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
} else {
    init();
}

// Re-init inside Shopify's theme editor when the section is re-rendered.
document.addEventListener('shopify:section:load', (event) => {
    if (event.target) init(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
    if (event.target) destroyIn(event.target);
});


